import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FiLock, FiEye, FiEyeOff, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { authAPI } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const { showError, showSuccess } = useNotification();
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [errors, setErrors] = useState({});
  const [resetSuccess, setResetSuccess] = useState(false);
  
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const validateResetToken = useCallback(async () => {
    if (!token) {
      setTokenValid(false);
      setValidatingToken(false);
      return;
    }
    
    try {
      await authAPI.validateResetToken(token);
      setTokenValid(true);
    } catch (error) {
      setTokenValid(false);
      showError('Invalid or expired reset link. Please request a new one.');
    } finally {
      setValidatingToken(false);
    }
  }, [token, showError]);

  useEffect(() => {
    validateResetToken();
  }, [validateResetToken]);

  

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and number';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      await authAPI.resetPassword({
        token,
        password: formData.password
      });
      setResetSuccess(true);
      showSuccess('Password reset successfully!');
    } catch (error) {
      showError(error.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (validatingToken) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="loading-state">
            <LoadingSpinner size="large" />
            <p>Validating reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="error-state">
            <div className="error-icon">
              <FiAlertCircle size={64} />
            </div>
            
            <div className="error-content">
              <h1>Invalid Reset Link</h1>
              <p>
                This password reset link is invalid or has expired. 
                Reset links are only valid for 24 hours for security reasons.
              </p>
              
              <div className="error-actions">
                <Link to="/forgot-password">
                  <Button variant="primary">
                    Request New Reset Link
                  </Button>
                </Link>
                
                <Link to="/login">
                  <Button variant="outline">
                    Back to Sign In
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (resetSuccess) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="success-state">
            <div className="success-icon">
              <FiCheckCircle size={64} />
            </div>
            
            <div className="success-content">
              <h1>Password Reset Complete</h1>
              <p>
                Your password has been successfully reset. You can now sign in 
                with your new password.
              </p>
              
              <div className="success-actions">
                <Link to="/login">
                  <Button variant="primary" size="large">
                    Sign In Now
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <h1>Create New Password</h1>
          <p>
            {email ? (
              <>Create a new password for <strong>{email}</strong></>
            ) : (
              'Create a strong password for your account'
            )}
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <div className="input-wrapper">
              <FiLock className="input-icon" size={18} />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`form-input ${errors.password ? 'error' : ''}`}
                placeholder="Enter your new password"
                autoComplete="new-password"
                autoFocus
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </button>
            </div>
            {errors.password && <span className="error-message">{errors.password}</span>}
            <div className="password-requirements">
              <p>Password must contain:</p>
              <ul>
                <li className={formData.password.length >= 8 ? 'valid' : ''}>
                  At least 8 characters
                </li>
                <li className={/[A-Z]/.test(formData.password) ? 'valid' : ''}>
                  One uppercase letter
                </li>
                <li className={/[a-z]/.test(formData.password) ? 'valid' : ''}>
                  One lowercase letter
                </li>
                <li className={/\d/.test(formData.password) ? 'valid' : ''}>
                  One number
                </li>
              </ul>
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <div className="input-wrapper">
              <FiLock className="input-icon" size={18} />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                placeholder="Confirm your new password"
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </button>
            </div>
            {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
          </div>
          
          <Button
            type="submit"
            variant="primary"
            size="large"
            disabled={loading}
            className="auth-button"
          >
            {loading ? (
              <LoadingSpinner size="small" />
            ) : (
              'Reset Password'
            )}
          </Button>
        </form>
        
        <div className="auth-footer">
          <Link to="/login" className="back-link">
            Back to Sign In
          </Link>
        </div>
        
        <div className="security-note">
          <p>
            <strong>Security Tip:</strong> Choose a password that's unique to this account 
            and not used elsewhere. Consider using a password manager to generate and 
            store strong passwords.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;